import { Injectable } from "@angular/core";
import { BaseService } from "./base.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { AnalysisType } from "../models/analysisType";
import { Observable, retry, catchError, Subject, mergeMap, map } from "rxjs";
import { BoundingBox } from "../models/boundingBox";
import { SatelliteHistory } from "../models/satelliteHistory";
import { PinAnalysisPeriod } from "../models/pinAnalysisPeriod";
import {MapsTypesAvailabilityDto} from '../dto/MapsTypesAvailabilityDto';
import {FarmAvailableMapsDto} from '../dto/FarmAvailableMapsDto';
import {FieldAvailableMapsDto} from '../dto/FieldAvailableMapsDto';
import {MapsFarmImageryDto} from '../dto/MapsFarmImageryDto';
import {FieldImageryLegendDto} from '../dto/FieldImageryLegendDto';
import {MapsFarmImageryLegendDto} from '../dto/MapsFarmImageryLegendDto';
import {MapsTypesAvailabilityModel} from '../models/mapsTypesAvailabilityModel';
import { AnalysisLegendStatsModel } from "../models/analysis-managment-models/analysis-stats-model/analysis-legend-stats-model";
import {AnalysisSequenceItem} from "../sequencing/sequnce-models/analysisSequenceItem";
import { VrtPreviewRequest, VrtPreviewResponse } from "../maps/utils/vrt-smoothing";


@Injectable({
    providedIn: 'root'
  })
  export class AnalysisManagementService extends BaseService {

    private API_ANALYSIS_MANAGEMENT_URL = this.API_BASE_URL + "analysis-management";

    constructor(private httpClient: HttpClient,
        public _snackBar: MatSnackBar,
        public cookieService: CookieService,
        public translate: TranslateService) {
            super()
      }

    // CAN REMOVE THIS IN THE FUTURE SINCE WE DIRECTLY PASS THE URL TO GOOGLE MAPS
    getImageAsBlob(url): Observable<any> {
      const headers = new HttpHeaders()
        .set('Access-Control-Allow-Headers', '*')
        .set('Access-Control-Allow-Origin', '*')
        .set('Access-Control-Allow-Methods', '*')
        .set('Access-Control-Allow-Credentials', 'true');
      return this.httpClient.get(url, { headers: headers, responseType: 'arraybuffer' })
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.BOUNDING-BOX'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
      }

    ////////////////////////////////////////////////////////////////////////////
    //////////////////                GET                     //////////////////
    ////////////////////////////////////////////////////////////////////////////

    getAnalysisTypes(historyId: number): Observable<AnalysisType[]> {
      return this.httpClient.get<AnalysisType[]>(this.API_ANALYSIS_MANAGEMENT_URL + "/findAnalysisTypesById?id=" + historyId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getAnalysisTypes2(historyId: number): Observable<AnalysisType[]> {
      return this.httpClient.get<AnalysisType[]>(this.API_ANALYSIS_MANAGEMENT_URL + "/findAnalysisTypesListById?id=" + historyId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getAnalysisTypeData(historyId: number): Observable<AnalysisType[]> {
      return this.httpClient.get<AnalysisType[]>(this.API_ANALYSIS_MANAGEMENT_URL + "/findAnalysisTypeDataById?id=" + historyId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS-TYPE'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getBoundingBox(analysisId: number, shapePropertyTypeId: number): Observable<BoundingBox> {
      return this.httpClient.get<BoundingBox>(this.API_ANALYSIS_MANAGEMENT_URL + "/findBoundingBox?analysisId=" + analysisId + "&shapePropertyTypeId=" + shapePropertyTypeId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.BOUNDING-BOX'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    /**
     * getBoundingBox without the snackbar or the retry, for the imagery hover
     * preview on the maps route.
     *
     * Hovering is not something the user asked to happen, and the pointer runs
     * down a list of captures, so a failing lookup there would raise a toast per
     * row. The caller falls back to the field's own extent instead and handles
     * the error itself. getBoundingBox above is unchanged for everyone else.
     */
    getBoundingBoxQuietly(analysisId: number, shapePropertyTypeId: number): Observable<BoundingBox> {
      return this.httpClient.get<BoundingBox>(this.API_ANALYSIS_MANAGEMENT_URL + "/findBoundingBox?analysisId=" + analysisId + "&shapePropertyTypeId=" + shapePropertyTypeId);
    }

    getAnalysisImage(historyId: number, analysisTypeId: number): Observable<string> {
      return this.httpClient.get<string>(this.API_ANALYSIS_MANAGEMENT_URL + "/analysisImage?analysisId=" + historyId + "&shapePropertyTypeId=" + analysisTypeId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getAnalysisColour(historyId: number, analysisTypeId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + historyId + "/getAnalysisColours?analysisId=&shapePropertyTypeId=" + analysisTypeId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS-COLOR'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getSatelliteHistory(fieldId: number): Observable<SatelliteHistory[]> {
      return this.httpClient.get<SatelliteHistory[]>(this.API_ANALYSIS_MANAGEMENT_URL + "/satelliteImageHistory?fieldId=" + fieldId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.SATELLITE-HISTORY'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    /**
     * Analysis at a point across EVERY sampling period.
     *
     * The mapping API's findForPin collapses to the newest period only, and its
     * response cannot change because both mobile apps read it by exact key
     * name. This is the separate all-periods endpoint.
     */
    getPinAnalysisAllPeriods(tenantId: number, fieldId: number, lat: number, lng: number): Observable<PinAnalysisPeriod[]> {
      return this.httpClient.get<PinAnalysisPeriod[]>(this.API_ANALYSIS_MANAGEMENT_URL +
          "/pinAnalysisAllPeriods?tenantId=" + tenantId + "&fieldId=" + fieldId + "&lat=" + lat + "&lng=" + lng)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.DYNAMIC-PINS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getAnalysisColoursArea(historyId: number, analysisTypeId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/getAnalysisColoursArea?analysisId=" + historyId + "&shapePropertyTypeId=" + analysisTypeId)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS-COLOR'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }


    //////////////////////////////////// MAPS ////////////////////////////////////



//maps component  Oninit FARM & FIELD - populate list with available analysis types
    getMapsTypesAvailability(mapsTypesAvailabilityDto:MapsTypesAvailabilityDto): Observable<MapsTypesAvailabilityModel[]> {
        return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/maps/types/availability" , mapsTypesAvailabilityDto )
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    /////////////////////////////////////// FARM ////////////////////////////////////////

//maps component FARM/mapping - get available list items
    getFarmAvailableMaps(farmAvailableMapsDto:FarmAvailableMapsDto): Observable<any> {
        return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/maps/farm/availability" , farmAvailableMapsDto)
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    getProfitabilityPolygonPoint(analysisId: number, longitude: number, latitude: number): Observable<any> {
        return this.httpClient.post<any>(this.API_BASE_URL + "v2/analysis-management/profitability/polygon/point", {
            analysisId,
            longitude,
            latitude
        })
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


    //maps component FARM/mapping - overlay images
    getFarmImagery(mapsFarmImageryDto:MapsFarmImageryDto): Observable<any> {
        return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/maps/farm/imagery" , mapsFarmImageryDto)
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    //maps component FARM/mapping - legend display
    getMapsFarmImageryLegend(mapsFarmImageryLegendDto:MapsFarmImageryLegendDto): Observable<any> {
        return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/maps/farm/imagery/legend" , mapsFarmImageryLegendDto)
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }



    /////////////////////////////////////// FIELD ////////////////////////////////////////

    //maps component FIELD - overlay images
    getAvailableMapsField(fieldAvailableMapsDto:FieldAvailableMapsDto): Observable<any> {
        return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/maps/field/imagery" , fieldAvailableMapsDto)
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    //maps component FIELD - get field imagery legends
    getFieldImageryLegend(fieldImageryLegendDto:FieldImageryLegendDto): Observable<any> {
        return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/maps/field/imagery/legend" , fieldImageryLegendDto)
            .pipe(
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    // Queued legend fetcher - limits concurrent requests to prevent 504 timeouts
    private legendQueue$ = new Subject<{ dto: FieldImageryLegendDto, callback: (result: any) => void }>();
    private legendQueueInitialized = false;

    private initLegendQueue(): void {
        if (this.legendQueueInitialized) return;
        this.legendQueueInitialized = true;
        this.legendQueue$.pipe(
            mergeMap(({ dto, callback }) =>
                this.getFieldImageryLegend(dto).pipe(
                    catchError(() => [null])
                ).pipe(
                    catchError(() => [null])
                ).pipe(
                    mergeMap(result => { callback(result); return []; })
                ), 3) // max 3 concurrent requests
        ).subscribe();
    }

    queueFieldImageryLegend(dto: FieldImageryLegendDto): Observable<any> {
        this.initLegendQueue();
        return new Observable(observer => {
            this.legendQueue$.next({
                dto,
                callback: (result) => {
                    observer.next(result);
                    observer.complete();
                }
            });
        });
    }

    getLegendMinMaxAverageStats(analysisId?: number, shapePropertyTypeId?: number): Observable<AnalysisLegendStatsModel> {
      return this.httpClient.get<AnalysisLegendStatsModel>(this.API_ANALYSIS_MANAGEMENT_URL + `/analysisStats?analysisId=${analysisId}&shapePropertyTypeId=${shapePropertyTypeId}`)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getAnalysisSequenceByFieldIdTypeIdCount(fieldId: number, analysisDefinitionId: number, analysisCount: number, fromDate?: string, toDate?: string): Observable<AnalysisSequenceItem[]> {
      let url = this.API_ANALYSIS_MANAGEMENT_URL + `/analysisSequence?fieldId=${fieldId}&analysisDefinitionId=${analysisDefinitionId}&analysisCount=${analysisCount}`;
      
      if (fromDate) {
        url += `&fromDate=${fromDate}`;
      }
      if (toDate) {
        url += `&toDate=${toDate}`;
      }

      return this.httpClient.get<AnalysisSequenceItem[]>(url)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }


    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             INSERT                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    generateProfitabilityMap(sourceAnalysisId: number, fieldId: number, inputCost: number, productPrice: number): Observable<any> {
      const body = { sourceAnalysisId, fieldId, inputCost, productPrice };
      return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/profitability", body)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getProfitabilityAnalyses(fieldId: number): Observable<any> {
      return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/profitability/analyses", { fieldId })
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getProfitabilityPolygons(analysisId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + `/profitability/polygons/${analysisId}`)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    getProfitabilitySummary(analysisId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + `/profitability/summary/${analysisId}`)
        .pipe(
          retry(1),
          catchError(err => {
            this.hasError.next(true);
            this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             UPDATE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////             DELETE                  ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    ////////////////////////////////////////////////////////////////////////////
    ////////////////////         ANALYSIS HISTORY            ///////////////////
    ////////////////////////////////////////////////////////////////////////////

    getAnalysisHistory(fieldId: number, type: string, startDate: string, endDate: string): Observable<any[]> {
        let params = '?fieldId=' + fieldId + '&type=' + encodeURIComponent(type) + '&startDate=' + startDate + '&endDate=' + endDate;
        return this.httpClient.get<any[]>(this.API_ANALYSIS_MANAGEMENT_URL + '/analysisHistory' + params)
            .pipe(
                retry(1),
                catchError(err => {
                    this.hasError.next(true);
                    this._snackBar.open(this.translate.instant('DATA-SERVICE.ANALYSIS'), this.translate.instant('SNACKBAR.DISMISS'), {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    // VRT
    listVrtForField(fieldId: number): Observable<any[]> {
      return this.httpClient.get<any[]>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/field/" + fieldId);
    }

    createVrt(payload: any): Observable<any> {
      return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt", payload);
    }

    regenerateVrt(analysisId: number, payload: any): Observable<any> {
      return this.httpClient.put<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/" + analysisId, payload);
    }

    /** A finished VRT's polygons, for drawing it on the map. */
    getVrtPolygons(analysisId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/" + analysisId + "/polygons");
    }

    /**
     * Any analysis's cached polygons, filtered to one property type.
     *
     * Separate from getVrtPolygons, which serves a finished VRT and is relied on
     * by the live map overlay. This one serves the SOURCE map a VRT is being
     * built from, which the builder recolours to preview the result.
     *
     * The property filter is the point: a chemical source carries a set of
     * polygons per element, and unfiltered they arrive in one array with nothing
     * to tell them apart.
     */
    getAnalysisPolygons(analysisId: number, propertyTypeId?: number): Observable<any> {
      const url = this.API_ANALYSIS_MANAGEMENT_URL + "/analysis/" + analysisId + "/polygons";
      return this.httpClient.get<any>(
        propertyTypeId != null ? url + "?propertyTypeId=" + propertyTypeId : url
      );
    }

    /**
     * A VRT's zones as they would come out at a smoothing level, without saving.
     *
     * Read only on the server. It can take several seconds on a large field, and
     * can fail outright (a 504 from API Gateway, or a 404 where the endpoint has
     * not been deployed yet). No snackbar and no retry here: the builder says
     * inline that the smoothed preview is unavailable and carries on with the
     * granular one, and retrying a call that timed out would only time out again.
     */
    previewVrt(body: VrtPreviewRequest): Observable<VrtPreviewResponse> {
      return this.httpClient.post<VrtPreviewResponse>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/preview", body);
    }

    getVrtDetails(analysisId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/" + analysisId);
    }

    finaliseVrt(analysisId: number): Observable<any> {
      return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/" + analysisId + "/finalise", { indemnificationAcknowledged: true });
    }

    createVrtNewVersion(analysisId: number): Observable<any> {
      return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/" + analysisId + "/new-version", {});
    }

    downloadVrtShapefile(analysisId: number): Observable<any> {
      return this.httpClient.get<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/vrt/" + analysisId + "/download?indemnificationAcknowledged=true");
    }

    getAnalysisLegendForVrt(fieldId: number, analysisType: string): Observable<any[]> {
      return this.httpClient.get<any[]>(this.API_ANALYSIS_MANAGEMENT_URL + "/findAnalysisTypesById?id=" + fieldId)
        .pipe(
          map((types: any[]) => {
            const matchingType = types.find(t => t.name === analysisType);
            if (matchingType && matchingType.analysisTypeLegend) {
              return matchingType.analysisTypeLegend.analysisTypeLegendProperties || [];
            }
            return [];
          })
        );
    }

    /////////////////////////////////// ON-REQUEST RECACHE ///////////////////////////////////

    // One already-resolved analysisId per call -- not fieldId+type+period, since every caller of
    // this already has analysisId straight from getAvailableMapsField/getFarmAvailableMaps (the
    // exact same backend's own prior response), and re-deriving via type+period would mean
    // guessing the real period string format for no benefit. The endpoint derives field_id/type/
    // tenant server-side from analysisId itself -- nothing else to pass, and no shape through
    // which "this whole type" or "everything" could be requested either way.
    recacheAnalysis(analysisId: number): Observable<any> {
      return this.httpClient.post<any>(this.API_ANALYSIS_MANAGEMENT_URL + "/recache", { analysisId })
        .pipe(
          catchError(err => {
            this.hasError.next(true);
            // The Lambda refuses a duplicate in-flight request for the exact same analysisId with a
            // 409, specifically so a spam-click (or a second tab, or a colleague clicking the same
            // thing) doesn't queue the same recache twice. That is a real, calm answer, not a
            // failure, so it gets its own message rather than the generic one below.
            const message = err.status === 409
              ? "This field is already recaching — please select another field, or wait a few minutes."
              : this.translate.instant('DATA-SERVICE.ANALYSIS');
            this._snackBar.open(message, this.translate.instant('SNACKBAR.DISMISS'), {
              duration: 5000,
            });
            this.hasError.next(false);
            return this.errorHandler(err);
          })
        );
    }

  }
